import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';

/**
 * WeatherWidget Component
 *
 * Props:
 *   compact    – Kleine Pill-Variante für die Mobile-Header-Leiste
 *   showInMenu – Breite Variante für das Mobile-Drawer-Menü
 *   (keine Props) – Standard-Variante für den Desktop-Header
 */
const WeatherWidget = ({ compact = false, showInMenu = false }) => {
    const navigate = useNavigate();
    const { fetchWithAuth } = useContext(AuthContext);
    const [weather, setWeather] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchWeather();
    }, []);

    const fetchWeather = async () => {
        try {
            if ('geolocation' in navigator) {
                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        const { latitude, longitude } = position.coords;
                        await getWeatherData(latitude, longitude);
                    },
                    () => getWeatherData(48.1351, 11.5820), // Fallback München
                    { timeout: 5000, enableHighAccuracy: false }
                );
            } else {
                getWeatherData(48.1351, 11.5820);
            }
        } catch {
            setLoading(false);
        }
    };

    const getWeatherData = async (lat, lon) => {
        try {
            const response = await fetchWithAuth(
                `/api/weather/current/?latitude=${lat}&longitude=${lon}`
            );
            if (!response.ok) throw new Error(`Weather API returned ${response.status}`);
            const data = await response.json();
            if (data.current) {
                setWeather({
                    temp: Math.round(data.current.temperature_2m),
                    code: data.current.weather_code ?? 0
                });
            }
            setLoading(false);
        } catch (error) {
            console.warn('WeatherWidget: Fetch failed.', error.message);
            setLoading(false);
        }
    };

    const getWeatherIcon = (code) => {
        if (code === 0 || code === 1) return '☀️';
        if (code === 2 || code === 3) return '⛅';
        if (code >= 45 && code <= 48) return '🌫️';
        if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return '🌧️';
        if (code >= 71 && code <= 77) return '❄️';
        if (code >= 95 && code <= 99) return '⛈️';
        return '🌤️';
    };

    const getWeatherDescription = (code) => {
        if (code === 0 || code === 1) return 'Sonnig';
        if (code === 2) return 'Teils bewölkt';
        if (code === 3) return 'Bewölkt';
        if (code >= 45 && code <= 48) return 'Neblig';
        if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return 'Regen';
        if (code >= 71 && code <= 77) return 'Schnee';
        if (code >= 95 && code <= 99) return 'Gewitter';
        return 'Bewölkt';
    };

    const handleClick = () => navigate('/weather');

    // ── Loading State ──────────────────────────────────────────────
    if (loading) {
        if (compact) {
            return (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg">
                    <div className="w-3 h-3 border-2 border-gray-300 border-t-plantlix-500 rounded-full animate-spin" />
                </div>
            );
        }
        if (showInMenu) {
            return (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-900">
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-plantlix-500 rounded-full animate-spin" />
                    <span className="text-xs text-gray-400">Wetter wird geladen…</span>
                </div>
            );
        }
        return (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-plantlix-500 rounded-full animate-spin" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Wetter…</span>
            </div>
        );
    }

    // ── No Data Fallback ───────────────────────────────────────────
    if (!weather) {
        if (compact) return null; // Im kompakten Modus nichts zeigen wenn kein Wetter
        if (showInMenu) {
            return (
                <button
                    onClick={handleClick}
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                    <Sun className="w-5 h-5" />
                    <span>Wetter nicht verfügbar</span>
                    <span className="ml-auto text-xs opacity-60">Details →</span>
                </button>
            );
        }
        return (
            <div
                onClick={handleClick}
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 cursor-pointer text-gray-400"
            >
                <Sun className="w-4 h-4" />
                <span className="text-xs">Wetter</span>
            </div>
        );
    }

    const icon = getWeatherIcon(weather.code);
    const desc = getWeatherDescription(weather.code);

    // ── compact: Mini-Pill neben dem Hamburger ─────────────────────
    if (compact) {
        return (
            <button
                onClick={handleClick}
                title={`${desc} – Für Vorhersage klicken`}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-800 dark:to-gray-700 rounded-lg border border-blue-100 dark:border-gray-600 hover:shadow-md transition-all active:scale-95"
            >
                <span className="text-base leading-none">{icon}</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white">{weather.temp}°</span>
            </button>
        );
    }

    // ── showInMenu: Breiter Menü-Eintrag im Drawer ─────────────────
    if (showInMenu) {
        return (
            <button
                onClick={handleClick}
                className="flex items-center gap-3 w-full px-3 py-3 rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50/60 dark:from-gray-800 dark:to-gray-800 border border-blue-100 dark:border-gray-700 hover:shadow-md transition-all active:scale-[0.98]"
                title={`${desc} – Vorhersage anzeigen`}
            >
                <span className="text-2xl leading-none">{icon}</span>
                <div className="flex flex-col items-start">
                    <span className="text-base font-black text-gray-900 dark:text-white leading-tight">{weather.temp}°C</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 leading-tight">{desc}</span>
                </div>
                <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">Details →</span>
            </button>
        );
    }

    // ── Standard: Desktop-Header ───────────────────────────────────
    return (
        <button
            onClick={handleClick}
            className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-800 dark:to-gray-700 rounded-lg border border-blue-100 dark:border-gray-600 hover:shadow-md transition-shadow cursor-pointer group"
            title={`${desc} – Für Vorhersage klicken`}
        >
            <span className="text-lg group-hover:scale-110 transition-transform">{icon}</span>
            <span className="text-sm font-bold text-gray-900 dark:text-white">{weather.temp}°C</span>
        </button>
    );
};

export default WeatherWidget;
